// External libraries
import React, { useEffect, useRef, useState } from "react";

// FontAwesome icons
import { faSearch } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Monaco Editor
import { DiffEditor, Editor } from "@monaco-editor/react";

// Kendo React components
import { process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridColumnMenuGroup,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";

// Internal components
import LoadingScreen from "../Pages/LoadingScreen/LoadingScreen";

// Audit Trail - Signals
import {
  auditStatus,
  auditTrailData,
  autoLoadEnabled,
  continuationToken,
  currentDays,
  currentView,
  hasMore,
} from "./AuditTrailSignals";

// Audit Trail - Utils
import {
  clearAuditTrail,
  getAuditTrail,
  getAuditTrailByDays,
  searchAuditTrail,
  setAutoLoadEnabled,
} from "./AuditTrailUtils";

// Styles
import "./ViewAuditTrail.css";

const AuditTrail = () => {
  const excelExportComponent = useRef(null);
  const gridContainerRef = useRef(null);
  const autoLoadTimeoutRef = useRef(null);
  const pageSize = 15;

  const [selectedDays, setSelectedDays] = useState(null);
  const days = [3, 7, 14, 30, 60, 90];

  const [selectedOldValueItem, setSelectedOldValueItem] = useState(null);
  const [selectedNewValueItem, setSelectedNewValueItem] = useState(null);
  const [selectedDiffItem, setSelectedDiffItem] = useState(null);
  const [filteredData, setFilteredData] = useState(auditTrailData.value);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isInitialSearchLoading, setIsInitialSearchLoading] = useState(false);

  const [dataState, setDataState] = useState({
    take: pageSize,
    skip: 0,
  });

  useEffect(() => {
    clearAuditTrail();
    getAuditTrail({ maxItemCount: 100 });
  }, []);

  useEffect(() => {
    setFilteredData(auditTrailData.value);
  }, [auditTrailData.value]);

  useEffect(() => {
    const shouldAutoLoad =
      autoLoadEnabled.value &&
      hasMore.value &&
      !isLoadingMore &&
      ((currentView.value === "byDays" && currentDays.value) ||
        (currentView.value === "search" && searchInputValue));

    if (shouldAutoLoad) {
      autoLoadTimeoutRef.current = setTimeout(() => {
        loadMoreData(100);
      }, 500);
    }

    return () => {
      if (autoLoadTimeoutRef.current) {
        clearTimeout(autoLoadTimeoutRef.current);
      }
    };
  }, [
    autoLoadEnabled.value,
    hasMore.value,
    isLoadingMore,
    currentDays.value,
    currentView.value,
    searchInputValue,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      if (!gridContainerRef.current || autoLoadEnabled.value) return;

      const container = gridContainerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreData(100);
      }
    };

    const gridContainer = gridContainerRef.current;
    if (gridContainer && !autoLoadEnabled.value) {
      gridContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (gridContainer) {
        gridContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    continuationToken.value,
    hasMore.value,
    isLoadingMore,
    currentDays.value,
    currentView.value,
    autoLoadEnabled.value,
  ]);

  const processedData = React.useMemo(() => {
    return process(filteredData ?? [], dataState);
  }, [filteredData, dataState]);

  const loadMoreData = async (itemCount = 100) => {
    if (hasMore.value && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        if (currentView.value === "topItems") {
          await getAuditTrail({
            maxItemCount: itemCount,
            continuationToken: continuationToken.value,
          });
        } else if (currentView.value === "byDays" && currentDays.value) {
          await getAuditTrailByDays({
            days: currentDays.value,
            maxItemCount: itemCount,
            continuationToken: continuationToken.value,
          });
        } else if (currentView.value === "search") {
          await searchAuditTrail({
            searchTerm: searchInputValue,
            maxItemCount: itemCount,
            continuationToken: continuationToken.value,
          });
        }
      } catch (error) {
        console.error("Error loading more data:", error);
        if (autoLoadEnabled.value) {
          setAutoLoadEnabled(false);
        }
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const handleManualLoadMore = () => {
    loadMoreData(100);
  };

  const exportToExcel = () => {
    if (excelExportComponent.current) {
      excelExportComponent.current.save();
    }
  };

  const handleDaysChange = (days) => {
    setSelectedDays(days);
    setSearchInputValue("");
    setDataState({
      take: pageSize,
      skip: 0,
    });
    clearAuditTrail();
    getAuditTrailByDays({ days, maxItemCount: 100 });
  };

  const handleShowTopItems = () => {
    setSelectedDays(null);
    setSearchInputValue("");
    setDataState({
      take: pageSize,
      skip: 0,
    });
    clearAuditTrail();
    getAuditTrail({ maxItemCount: 100 });
  };

  const dataStateChange = (event) => {
    setDataState(event.dataState);
  };

  const handleSearchInputChange = (e) => {
    const searchValue = e.target.value;
    setSearchInputValue(searchValue);

    if (searchValue.length === 0) {
      setIsInitialSearchLoading(false);
      setDataState({
        take: pageSize,
        skip: 0,
      });
      clearAuditTrail();
      if (selectedDays) {
        getAuditTrailByDays({ days: selectedDays, maxItemCount: 100 });
      } else {
        getAuditTrail({ maxItemCount: 100 });
      }
    }
  };

  const handleSearchButtonClick = () => {
    const searchValue = searchInputValue.trim();

    if (searchValue.length === 0) {
      setIsInitialSearchLoading(false);
      setDataState({
        take: pageSize,
        skip: 0,
      });
      clearAuditTrail();
      if (selectedDays) {
        getAuditTrailByDays({ days: selectedDays, maxItemCount: 100 });
      } else {
        getAuditTrail({ maxItemCount: 100 });
      }
      return;
    }

    if (searchValue.length < 2) {
      alert("Please enter at least 2 characters to search.");
      return;
    }

    setSelectedDays(null);
    setDataState({
      take: pageSize,
      skip: 0,
    });

    setIsInitialSearchLoading(true);
    clearAuditTrail();
    searchAuditTrail({
      searchTerm: searchValue,
      maxItemCount: 100,
    }).finally(() => {
      setIsInitialSearchLoading(false);
    });
  };

  const handleSearchInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchButtonClick();
    }
  };

  const renderOldDataColumn = (e) => {
    return (
      <td {...e.tdProps}>
        {e.dataItem.oldValue && (
          <Button
            type="button"
            onClick={() => setSelectedOldValueItem(e.dataItem)}
            className="!k-ml-4"
          >
            Show Details
          </Button>
        )}
      </td>
    );
  };

  const renderNewDataColumn = (e) => {
    return (
      <td {...e.tdProps}>
        {e.dataItem.newValue && (
          <Button
            type="button"
            onClick={() => setSelectedNewValueItem(e.dataItem)}
            className="!k-ml-4"
          >
            Show Details
          </Button>
        )}
      </td>
    );
  };

  const renderDiffDataColumn = (e) => {
    return (
      <td>
        {e.dataItem.actionType === "UPDATE" && (
          <Button
            type="button"
            onClick={() => setSelectedDiffItem(e.dataItem)}
            className="!k-ml-4"
          >
            Show Details
          </Button>
        )}
      </td>
    );
  };

  const showLoadingScreen =
    (auditStatus.value === "loading" && auditTrailData.value.length === 0) ||
    isInitialSearchLoading;

  return (
    <div>
      {showLoadingScreen ? (
        <div>
          <LoadingScreen />
        </div>
      ) : (
        <>
          <h1>Audit Trail Log</h1>
          <div
            className="grid-container"
            ref={gridContainerRef}
            style={{ maxHeight: "800px", overflow: "auto" }}
          >
            <ExcelExport
              data={processedData.data}
              ref={excelExportComponent}
            >
              <Grid
                data={processedData}
                onDataStateChange={dataStateChange}
                pageable={true}
                pageSize={pageSize}
                resizable={true}
                reorderable={true}
                sortable={true}
              >
                <GridToolbar>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Input
                      name="txtAuditTrailSearchBar"
                      placeholder="Search..."
                      value={searchInputValue}
                      onChange={handleSearchInputChange}
                      onKeyDown={handleSearchInputKeyPress}
                      className="auditSearch"
                      style={{ marginRight: "0" }}
                    />
                    <Button
                      onClick={handleSearchButtonClick}
                      className="k-button"
                      title="Search"
                      style={{
                        minWidth: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "10px",
                      }}
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </div>

                  <DropDownList
                    name="days"
                    label="Days"
                    data={days}
                    value={selectedDays}
                    onChange={(e) => {
                      handleDaysChange(e.value);
                    }}
                  />

                  {selectedDays && (
                    <Button
                      onClick={handleShowTopItems}
                      className="k-button"
                      style={{ marginLeft: "10px" }}
                    >
                      Show Top Items
                    </Button>
                  )}

                  <Button
                    title="Export to Excel"
                    onClick={exportToExcel}
                    style={{
                      position: "absolute",
                      right: 5,
                    }}
                  >
                    Export to Excel
                  </Button>
                </GridToolbar>

                <Column
                  field="timestamp"
                  title="Timestamp"
                  filter="text"
                  sortable
                  cells={{
                    data: renderColumnTimestamp,
                  }}
                />

                <Column
                  field="userId"
                  title="User ID"
                  filter="text"
                  columnMenu={ColumnMenu}
                />
                <Column
                  field="actionType"
                  title="Action Type"
                  filter="text"
                  filterable={true}
                  columnMenu={ColumnMenu}
                />
                <Column
                  field="resourceType"
                  title="Resource Type"
                  filter="text"
                  filterable={true}
                  columnMenu={ColumnMenu}
                />
                <Column
                  field="resourceId"
                  title="Resource ID"
                  filter="text"
                  columnMenu={ColumnMenu}
                />
                <Column
                  field="oldValue"
                  title="Old Value"
                  cells={{
                    data: (e) => renderOldDataColumn(e),
                  }}
                />

                <Column
                  field="newValue"
                  title="New Value"
                  cells={{
                    data: (e) => renderNewDataColumn(e),
                  }}
                />

                <Column
                  field="diff"
                  title="Diff"
                  cells={{
                    data: (e) => renderDiffDataColumn(e),
                  }}
                />
              </Grid>
            </ExcelExport>
          </div>
        </>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "5px",
        }}
      >
        {(isLoadingMore ||
          (auditStatus.value === "loading" && auditTrailData.value?.length > 0)) && (
          <div className="text-center py-2">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="ml-2">{autoLoadEnabled.value ? "" : "Loading more data..."}</span>
          </div>
        )}

        {!hasMore.value && auditTrailData.value?.length > 0 && (
          <div className="text-center py-2 text-gray-500">
            All data has been loaded ({auditTrailData.value?.length} items)
          </div>
        )}

        {hasMore.value && !isLoadingMore && !autoLoadEnabled.value && (
          <div className="text-center py-2">
            <Button
              onClick={handleManualLoadMore}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              Load More Data (100 items)
            </Button>
          </div>
        )}

        {autoLoadEnabled.value && hasMore.value && (
          <div className="text-center py-2 text-blue-500">Loading data ...</div>
        )}
      </div>

      {selectedOldValueItem && (
        <Window
          title="Old Value Details"
          onClose={() => setSelectedOldValueItem(null)}
          initialHeight={650}
          initialWidth={1000}
        >
          <Editor
            height="100%"
            language="json"
            value={JSON.stringify(JSON.tryParse(selectedOldValueItem.oldValue, "{}"), null, 2)}
            options={{
              readOnly: true,
              minimap: { enabled: true },
            }}
            loading={<div>Loading...</div>}
          />
        </Window>
      )}

      {selectedNewValueItem && (
        <Window
          title="New Value Details"
          onClose={() => setSelectedNewValueItem(null)}
          initialHeight={650}
          initialWidth={1000}
        >
          <Editor
            height="100%"
            language="json"
            value={JSON.stringify(JSON.tryParse(selectedNewValueItem.newValue, "{}"), null, 2)}
            options={{
              readOnly: true,
              minimap: { enabled: true },
            }}
            loading={<div>Loading...</div>}
          />
        </Window>
      )}

      {selectedDiffItem && (
        <Window
          title="Diff Details"
          onClose={() => setSelectedDiffItem(null)}
          initialHeight={650}
          initialWidth={1000}
        >
          <DiffEditor
            height="100%"
            language="json"
            original={JSON.stringify(JSON.tryParse(selectedDiffItem.oldValue, {}), null, 2)}
            modified={JSON.stringify(JSON.tryParse(selectedDiffItem.newValue, {}), null, 2)}
            keepCurrentOriginalModel={true}
            keepCurrentModifiedModel={true}
            options={{
              readOnly: true,
              renderSideBySide: true,
              automaticLayout: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
            }}
            loading={<div>Loading...</div>}
          />
        </Window>
      )}
    </div>
  );
};

export default AuditTrail;

const ColumnMenu = (event) => {
  return (
    <div>
      <GridColumnMenuGroup {...event} />
    </div>
  );
};

const renderColumnTimestamp = (event) => {
  return (
    <td>
      {new Date(event.dataItem.timestamp).toLocaleString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}
    </td>
  );
};
